import { useEffect, useState } from 'react';
import { eventImageSrc } from '../../utils/eventImage';

/** The note kept with a picture cleared on this page. */
export const CLEARANCE_NOTE = 'Einzeln freigegeben im Dashboard';

/** A small version of the picture: the backend's Instagram proxy, 360 px wide. */
export const thumbnail = (image) => {
  const src = eventImageSrc(image);
  if (!src || !src.includes('/live-radar/instagram/')) return src;
  return `${src}${src.includes('?') ? '&' : '?'}w=360`;
};

export const formatDay = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return '';
  return date.toLocaleDateString('de-AT', { weekday: 'short', day: '2-digit', month: '2-digit' });
};

export const formatDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return '';
  return date.toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

/** "Instagram" or "Web · flucc.at". */
export const sourceLabel = (picture) =>
  picture.source === 'INSTAGRAM' ? 'Instagram' : `Web${picture.sourceHost ? ` · ${picture.sourceHost}` : ''}`;

/** When the picture is: the event's day, "Club", or nothing. */
export const whenLabel = (picture) =>
  picture.eventDate ? formatDay(picture.eventDate) : picture.type === 'Club' ? 'Club' : '';

/** The other markers the same picture stands on (the first use is the picture's own title). */
export const otherUses = (picture) => (Array.isArray(picture.uses) ? picture.uses.slice(1) : []);

/** "Rave · Sa., 18.10." for one use of a picture. */
export const usageLabel = (use) => {
  const when = use.eventDate ? formatDay(use.eventDate) : use.type === 'Club' ? 'Club' : '';
  return `${use.title}${when ? ` · ${when}` : ''}${use.eventDate && !use.upcoming ? ' (vorbei)' : ''}`;
};

/** "Auch bei 2 weiteren Events", or '' when the picture stands on one marker only. */
export const otherUsesLabel = (picture) => {
  const count = otherUses(picture).length;
  if (count === 0) return '';
  return `Auch bei ${count === 1 ? '1 weiteren Event' : `${count} weiteren Events`}`;
};

/** The tooltip of "Entfernen": what removing a picture does, and how many markers lose it. */
export const removeTitle = (picture) => {
  const count = Array.isArray(picture.uses) ? picture.uses.length : 1;
  return `Bild ${count > 1 ? `bei allen ${count} Events/Clubs` : 'beim Event'} löschen – `
    + 'dort steht dann die Schick-Grafik. Lässt sich nicht rückgängig machen.';
};

/**
 * A button action that needs a second click within a few seconds (like removing a
 * profile picture), unless {@code needsConfirm} is false.
 */
export function useArmedClick(action, needsConfirm = true) {
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (!armed) return undefined;
    const timer = setTimeout(() => setArmed(false), 3500);
    return () => clearTimeout(timer);
  }, [armed]);
  const click = () => {
    if (needsConfirm && !armed) { setArmed(true); return; }
    setArmed(false);
    action();
  };
  return [armed, click];
}
